import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import type { CockpitPolicyView, CockpitPreviewView, ControllerLever } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import {
  DEMAND_LEVERS,
  EDITOR_MODES,
  SUPPLY_LEVERS,
  draftFromPolicy,
  draftToPolicy,
  effectiveDifferences,
  incentiveCockpitAuthority,
  previewLeverRows,
  previewRows,
  windowsToMinutes,
  type LeverDraft,
  type PolicyEditorDraft,
} from '../../../src/index';
import { useAdmin } from '../../session';
import { useT } from '../../i18n/provider';
import { useVocab } from '../../i18n/vocab';
import { Badge, Button, CheckRow, EmptyState, Field, Grid, MoneyInput, Notice, Panel, Row, SegmentedControl, Select, Stack, Table, Text, TextInput } from '@vello/web-theme';

/**
 * The controller's rule editor (W10-INCENTIVE-COCKPIT, D497, ADR-0117; doc 17 sections 3.4 and 3.6): the operator reads the governed `incentives.controllerPolicy` in force for the market,
 * changes the mode, the rhythm, the thresholds that classify a zone, the rule table, the caps of each lever and the pacing, SEES what the draft would have made of yesterday's windows beside
 * what the policy in force makes (a read-only classification preview: no money moves, nothing is saved), and PROPOSES the new version through the EXISTING governed-settings four-eyes flow -
 * a second person approves it on the governed-rules screen; this panel has no other way to change a policy. The governed key's own validator has the last word: a refused draft is told in words
 * (never a code). Money is typed through `MoneyInput` in the market's currency; percentages in percent.
 */
type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string } | undefined;

/**
 * The unsaved draft of each market survives a switch of tab (the panel unmounts) - but only for the same published version: a newer one starts over. The store belongs to the cockpit page
 * (it dies with it: a draft never outlives the page, a sign-out or another operator).
 */
export type RulesDrafts = Map<string, { readonly version: string; readonly draft: PolicyEditorDraft; readonly reason: string }>;
const versionKey = (view: CockpitPolicyView): string => (view.published === null ? 'none' : `${view.published.scope}:${String(view.published.version)}`);

export function RulesPanel({ market, drafts }: { readonly market: string; readonly drafts: RulesDrafts }): ReactElement {
  const { runtime, grants } = useAdmin();
  const { t, lang } = useT();
  const { v, fail } = useVocab();
  const { mayPropose } = incentiveCockpitAuthority(grants);
  const [loaded, setLoaded] = useState<CockpitPolicyView | undefined>(undefined);
  const [currency, setCurrency] = useState<string | null>(null);
  const [draft, setDraft] = useState<PolicyEditorDraft>(() => draftFromPolicy(null));
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<CockpitPreviewView | undefined>(undefined);
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const previewSeq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++seq.current;
    if (market === '') return;
    const [policy, spend] = await Promise.all([runtime.incentiveCockpit.policy(market), runtime.incentiveCockpit.spend(market)]);
    if (mine !== seq.current) return;
    if (!policy.ok) {
      setLoaded(undefined);
      setMsg({ tone: 'danger', text: t('cpt.rules.error', { code: fail(policy.error) }) });
      return;
    }
    setLoaded(policy.value);
    // W10K-01: the editor starts from the version as PUBLISHED, never from the effective one (a country's tightening and the shadow floor are shown beside it, read-only).
    setCurrency(policy.value.published?.value.currency ?? policy.value.value?.currency ?? (spend.ok ? spend.value.currency : null));
    const kept = drafts.get(market);
    if (kept !== undefined && kept.version === versionKey(policy.value)) {
      setDraft(kept.draft);
      setReason(kept.reason);
    } else {
      setDraft(draftFromPolicy(policy.value.published?.value ?? null));
      setReason('');
    }
    previewSeq.current += 1;
    setPreview(undefined);
    setMsg(undefined);
  }, [runtime, market, t, fail]);

  useEffect(() => {
    setLoaded(undefined);
    void load();
    // Reload when the header scope names another market.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);

  // The draft is kept per market while the panel is away (a tab switch); the published version it started from decides whether it is still good.
  useEffect(() => {
    if (loaded !== undefined) drafts.set(market, { version: versionKey(loaded), draft, reason });
  }, [drafts, market, loaded, draft, reason]);

  const set = <K extends keyof PolicyEditorDraft>(key: K, value: PolicyEditorDraft[K]): void => {
    setDraft((d) => ({ ...d, [key]: value }));
    previewSeq.current += 1; // a preview of the OLD draft still in flight must never land on the new one
    setBusy(false);
    setPreview(undefined);
  };
  const setLever = (lever: ControllerLever, patch: Partial<LeverDraft>): void => {
    setDraft((d) => ({ ...d, levers: { ...d.levers, [lever]: { ...d.levers[lever], ...patch } } }));
    previewSeq.current += 1;
    setBusy(false);
    setPreview(undefined);
  };
  const toggleRule = (key: 'supplyRules' | 'demandRules', lever: ControllerLever, on: boolean): void => set(key, on ? [...draft[key], lever] : draft[key].filter((l) => l !== lever));

  const build = (): ReturnType<typeof draftToPolicy> => draftToPolicy(draft, currency, loaded?.published?.value.pacing.hourlyCurveBp);
  const buildIssueText = (r: Extract<ReturnType<typeof draftToPolicy>, { ok: false }>): string => {
    const [head, tail] = r.field.includes('.') ? (r.field.split('.') as [string, string]) : [undefined, r.field];
    const label = t(`cpt.rules.field.${tail}`);
    return t(`cpt.rules.draft.${r.issue}`, { field: head === undefined ? label : `${v('active_incentive.lever', head)} · ${label}` });
  };

  const runPreview = async (): Promise<void> => {
    const built = build();
    if (!built.ok) return setMsg({ tone: 'danger', text: buildIssueText(built) });
    const mine = ++previewSeq.current;
    setBusy(true);
    const res = await runtime.incentiveCockpit.preview(market, built.value);
    // W10K-06: only the LATEST preview may answer; a slow earlier one (or one of a draft since edited) is dropped
    if (mine !== previewSeq.current) return;
    setBusy(false);
    if (!res.ok) return setMsg({ tone: 'danger', text: t('cpt.rules.error', { code: fail(res.error) }) });
    if (!res.value.valid) {
      setPreview(undefined);
      return setMsg({ tone: 'danger', text: t('cpt.rules.refused', { why: v('controller.policy_issue', res.value.issue) }) });
    }
    setMsg(undefined);
    setPreview(res.value);
  };

  const propose = async (): Promise<void> => {
    const built = build();
    if (!built.ok) return setMsg({ tone: 'danger', text: buildIssueText(built) });
    if (reason.trim().length < 5) return setMsg({ tone: 'danger', text: t('cpt.rules.reasonShort') });
    setBusy(true);
    const res = await runtime.settings.propose('incentives.controllerPolicy', { scope: 'market', scopeRef: market, value: built.value, reason: reason.trim() });
    setBusy(false);
    if (!res.ok) return setMsg({ tone: 'danger', text: t('cpt.rules.error', { code: fail(res.error) }) });
    setReason('');
    drafts.delete(market);
    setMsg({ tone: 'success', text: t('cpt.rules.proposed') });
  };

  const num = (id: string, label: string, value: string, onChange: (text: string) => void, hint?: string): ReactElement => (
    <Field label={label} htmlFor={`cpt-${id}`} {...(hint !== undefined ? { hint } : {})}>
      <TextInput id={`cpt-${id}`} className="tnum" inputMode="decimal" value={value} onChange={(ev) => onChange(ev.target.value)} />
    </Field>
  );
  const money = (id: string, label: string, value: bigint | undefined, onChange: (v: bigint | undefined) => void): ReactElement | null =>
    currency !== null && isIso4217Currency(currency) ? (
      <MoneyInput id={`cpt-${id}`} label={label} currency={currency} locale={lang} valueMinor={value} onChangeMinor={onChange} issueText={(i) => v('format.money_input_issue', i)} />
    ) : null;

  if (market === '') return <Panel><EmptyState title={t('cpt.rules.pickMarket')} /></Panel>;
  if (loaded === undefined) return msg !== undefined ? <Notice tone={msg.tone}>{msg.text}</Notice> : <Panel><EmptyState title={t('ui.loading')} /></Panel>;

  const policyState = loaded.policy.state;
  // What the policy IN FORCE has beyond the published version (a country's tightening, the shadow floor): shown, never written into the form.
  const differences = loaded.published !== null && loaded.value !== null ? effectiveDifferences(loaded.published.value, loaded.value) : [];
  const differenceLabel = (d: (typeof differences)[number]): string =>
    d.kind === 'field' ? t(`cpt.rules.field.${d.key}`) : d.kind === 'lever' ? v('active_incentive.lever', d.lever) : d.kind === 'rules' ? t('cpt.rules.s.rules') : t('cpt.rules.s.pacing');
  const leverCard = (lever: ControllerLever): ReactElement => {
    const d = draft.levers[lever];
    const isDiscount = lever === 'RIDER_ZONE_DISCOUNT';
    return (
      <Panel key={lever}>
        <Stack gap={3}>
          <h4>{v('active_incentive.lever', lever)}</h4>
          <CheckRow id={`cpt-${lever}-enabled`} checked={d.enabled} onChange={(on) => setLever(lever, { enabled: on })}>{t('cpt.rules.lever.enabled')}</CheckRow>
          <Grid min="xs" gap={3}>
            {isDiscount && num(`${lever}-max`, t('cpt.rules.field.maxPercent'), d.maxPercent, (x) => setLever(lever, { maxPercent: x }))}
            {money(`${lever}-unit`, t(isDiscount ? 'cpt.rules.field.maxPerRide' : 'cpt.rules.field.maxPerTrip'), d.maxPerUnit, (x) => setLever(lever, { maxPerUnit: x }))}
            {money(`${lever}-cap`, t('cpt.rules.field.maxSpendCap'), d.maxSpendCap, (x) => setLever(lever, { maxSpendCap: x }))}
            {num(`${lever}-floor`, t('cpt.rules.field.floorPercent'), d.floorPercent, (x) => setLever(lever, { floorPercent: x }))}
            {num(`${lever}-step`, t('cpt.rules.field.stepPercent'), d.stepPercent, (x) => setLever(lever, { stepPercent: x }))}
            {num(`${lever}-cool`, t('cpt.rules.field.cooldownMinutes'), d.cooldownMinutes, (x) => setLever(lever, { cooldownMinutes: x }))}
          </Grid>
        </Stack>
      </Panel>
    );
  };

  return (
    <Stack gap={4}>
      {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
      <Panel>
        <Stack gap={2}>
          <Row gap={2} align="center" wrap>
            <Text role="caption" tone="tertiary">{t('ctl.state.policy')}</Text>
            <Badge tone={policyState === 'ACTIVE' ? 'success' : policyState === 'INVALID' ? 'danger' : policyState === 'DISABLED' ? 'warning' : 'neutral'}>{v('incentive_fund.policy_state', policyState)}</Badge>
            <Text role="caption" tone="tertiary">{t('ctl.state.version', { version: loaded.policy.policyVersion })}</Text>
          </Row>
          <Text role="caption" tone="tertiary">{t('cpt.rules.intro')}</Text>
          {loaded.published === null && <Notice tone="warning">{t('cpt.rules.unset')}</Notice>}
          {loaded.published?.scope === 'country' && <Notice tone="neutral">{t('cpt.rules.countryOnly')}</Notice>}
          {loaded.published !== null && loaded.value !== null && differences.length > 0 && (
            <Notice tone="neutral">{t('cpt.rules.effectiveDiffers', { fields: differences.map(differenceLabel).join(', ') })}</Notice>
          )}
          {currency === null && <Notice tone="danger">{t('cpt.rules.noCurrency')}</Notice>}
        </Stack>
      </Panel>

      <Panel>
        <Stack gap={3}>
          <h3>{t('cpt.rules.s.rhythm')}</h3>
          <Grid min="xs" gap={3}>
            <Field label={t('cpt.rules.field.mode')} htmlFor="cpt-mode">
              <Select id="cpt-mode" value={draft.mode} onChange={(ev) => set('mode', EDITOR_MODES.find((m) => m === ev.target.value) ?? draft.mode)}>
                {EDITOR_MODES.map((m) => (
                  <option key={m} value={m}>{v('controller.mode', m)}</option>
                ))}
              </Select>
            </Field>
            {num('shadow', t('cpt.rules.field.minShadowDays'), draft.minShadowDays, (x) => set('minShadowDays', x))}
            {num('interval', t('cpt.rules.field.intervalSeconds'), draft.intervalSeconds, (x) => set('intervalSeconds', x))}
            {num('ttl', t('cpt.rules.field.decisionTtlMinutes'), draft.decisionTtlMinutes, (x) => set('decisionTtlMinutes', x))}
            {num('lookback', t('cpt.rules.field.lookbackWindows'), draft.lookbackWindows, (x) => set('lookbackWindows', x))}
            {num('age', t('cpt.rules.field.maxSignalAgeSeconds'), draft.maxSignalAgeSeconds, (x) => set('maxSignalAgeSeconds', x))}
          </Grid>
        </Stack>
      </Panel>

      <Panel>
        <Stack gap={3}>
          <h3>{t('cpt.rules.s.thresholds')}</h3>
          <Grid min="xs" gap={3}>
            {num('minreq', t('cpt.rules.field.minRequests'), draft.minRequests, (x) => set('minRequests', x))}
          </Grid>
          <h4>{v('controller.zone_state', 'SUPPLY_SHORT')}</h4>
          <Grid min="xs" gap={3}>
            {num('s-enter', t('cpt.rules.field.supplyEnterRate'), draft.supplyEnterRate, (x) => set('supplyEnterRate', x))}
            {num('s-exit', t('cpt.rules.field.supplyExitRate'), draft.supplyExitRate, (x) => set('supplyExitRate', x))}
            {num('s-eeta', t('cpt.rules.field.supplyEnterEta'), draft.supplyEnterEta, (x) => set('supplyEnterEta', x))}
            {num('s-xeta', t('cpt.rules.field.supplyExitEta'), draft.supplyExitEta, (x) => set('supplyExitEta', x))}
          </Grid>
          <h4>{v('controller.zone_state', 'DEMAND_SHORT')}</h4>
          <Grid min="xs" gap={3}>
            {num('d-free', t('cpt.rules.field.demandMinDriversFree'), draft.demandMinDriversFree, (x) => set('demandMinDriversFree', x))}
            {num('d-quotes', t('cpt.rules.field.demandMinQuotes'), draft.demandMinQuotes, (x) => set('demandMinQuotes', x))}
            {num('d-eu', t('cpt.rules.field.demandEnterUtil'), draft.demandEnterUtil, (x) => set('demandEnterUtil', x))}
            {num('d-xu', t('cpt.rules.field.demandExitUtil'), draft.demandExitUtil, (x) => set('demandExitUtil', x))}
            {num('d-ec', t('cpt.rules.field.demandEnterConv'), draft.demandEnterConv, (x) => set('demandEnterConv', x))}
            {num('d-xc', t('cpt.rules.field.demandExitConv'), draft.demandExitConv, (x) => set('demandExitConv', x))}
          </Grid>
          <h4>{v('controller.zone_state', 'QUIET')}</h4>
          <Grid min="xs" gap={3}>
            {num('q-quotes', t('cpt.rules.field.quietMaxQuotes'), draft.quietMaxQuotes, (x) => set('quietMaxQuotes', x))}
            {num('q-drivers', t('cpt.rules.field.quietMaxDrivers'), draft.quietMaxDrivers, (x) => set('quietMaxDrivers', x))}
          </Grid>
        </Stack>
      </Panel>

      <Panel>
        <Stack gap={3}>
          <h3>{t('cpt.rules.s.rules')}</h3>
          <Text role="caption" tone="tertiary">{t('cpt.rules.rulesHint')}</Text>
          <Grid min="md" gap={4}>
            <Stack gap={2}>
              <h4>{v('controller.zone_state', 'SUPPLY_SHORT')}</h4>
              {SUPPLY_LEVERS.map((l) => (
                <CheckRow key={l} id={`cpt-rule-s-${l}`} checked={draft.supplyRules.includes(l)} onChange={(on) => toggleRule('supplyRules', l, on)}>{v('active_incentive.lever', l)}</CheckRow>
              ))}
            </Stack>
            <Stack gap={2}>
              <h4>{v('controller.zone_state', 'DEMAND_SHORT')}</h4>
              {DEMAND_LEVERS.map((l) => (
                <CheckRow key={l} id={`cpt-rule-d-${l}`} checked={draft.demandRules.includes(l)} onChange={(on) => toggleRule('demandRules', l, on)}>{v('active_incentive.lever', l)}</CheckRow>
              ))}
            </Stack>
          </Grid>
        </Stack>
      </Panel>

      <Grid min="lg" gap={4}>
        {leverCard('RIDER_ZONE_DISCOUNT')}
        {leverCard('DRIVER_BOOST')}
        {leverCard('COURIER_BOOST')}
      </Grid>

      <Panel>
        <Stack gap={3}>
          <h3>{t('cpt.rules.s.pacing')}</h3>
          <Grid min="xs" gap={3}>
            {num('share', t('cpt.rules.field.dayBudgetSharePercent'), draft.dayBudgetSharePercent, (x) => set('dayBudgetSharePercent', x))}
            {money('daymax', t('cpt.rules.field.maxDayBudget'), draft.maxDayBudget, (x) => set('maxDayBudget', x))}
            {num('minscale', t('cpt.rules.field.minScalePercent'), draft.minScalePercent, (x) => set('minScalePercent', x))}
          </Grid>
          {loaded.published !== null && (
            <SegmentedControl<'KEEP' | 'FLAT'>
              ariaLabel={t('cpt.rules.field.curve')}
              value={draft.curve}
              onChange={(c) => set('curve', c)}
              options={[{ id: 'KEEP', label: t('cpt.rules.curve.keep') }, { id: 'FLAT', label: t('cpt.rules.curve.flat') }]}
            />
          )}
        </Stack>
      </Panel>

      <Panel>
        <Stack gap={3}>
          <h3>{t('cpt.rules.s.preview')}</h3>
          <Text role="caption" tone="tertiary">{t('cpt.rules.previewHint')}</Text>
          <Row gap={3} align="center" wrap>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => void runPreview()}>{t('cpt.rules.previewRun')}</Button>
          </Row>
          {preview !== undefined && (
            <Stack gap={3}>
              <Text role="caption" tone="tertiary"><span className="tnum">{t('cpt.rules.previewRead', { windows: preview.windowsRead, zones: preview.zonesRead })}</span></Text>
              {preview.zonesRead === 0 && <Notice tone="warning">{t('cpt.rules.previewNoSignals')}</Notice>}
              {preview.truncated && <Notice tone="warning">{t('cpt.rules.previewTruncated')}</Notice>}
              {preview.countryApplied && <Text role="caption" tone="tertiary">{t('cpt.rules.previewCountry')}</Text>}
              {preview.current === null && <Notice tone="neutral">{t('cpt.rules.previewNoCurrent')}</Notice>}
              <Table
                columns={[
                  { key: 'state', header: t('cpt.rules.col.state'), render: (r: ReturnType<typeof previewRows>[number]) => <span>{v('controller.zone_state', r.state)}</span> },
                  { key: 'current', header: t('cpt.rules.col.current'), numeric: true, render: (r: ReturnType<typeof previewRows>[number]) => <span className="tnum">{r.current === null ? '—' : t('cpt.rules.minutes', { minutes: windowsToMinutes(r.current) })}</span> },
                  { key: 'draft', header: t('cpt.rules.col.draft'), numeric: true, render: (r: ReturnType<typeof previewRows>[number]) => <span className="tnum">{t('cpt.rules.minutes', { minutes: windowsToMinutes(r.draft) })}</span> },
                ]}
                rows={previewRows(preview.draft, preview.current)}
                rowKey={(r) => r.state}
                empty={<EmptyState title={t('cpt.rules.previewEmpty')} />}
              />
              <Table
                columns={[
                  { key: 'lever', header: t('cpt.rules.col.lever'), render: (r: ReturnType<typeof previewLeverRows>[number]) => <span>{v('active_incentive.lever', r.lever)}</span> },
                  { key: 'current', header: t('cpt.rules.col.currentCalls'), numeric: true, render: (r: ReturnType<typeof previewLeverRows>[number]) => <span className="tnum">{r.current === null ? '—' : t('cpt.rules.minutes', { minutes: windowsToMinutes(r.current) })}</span> },
                  { key: 'draft', header: t('cpt.rules.col.draftCalls'), numeric: true, render: (r: ReturnType<typeof previewLeverRows>[number]) => <span className="tnum">{t('cpt.rules.minutes', { minutes: windowsToMinutes(r.draft) })}</span> },
                ]}
                rows={previewLeverRows(preview.draft, preview.current)}
                rowKey={(r) => r.lever}
                empty={<EmptyState title={t('cpt.rules.previewEmpty')} />}
              />
              <Text role="caption" tone="tertiary"><span className="tnum">{t('cpt.rules.previewChanges', { draft: preview.draft.totals.changes, current: preview.current === null ? '—' : preview.current.totals.changes })}</span></Text>
              <Text role="caption" tone="tertiary">{t('cpt.rules.previewScope')}</Text>
            </Stack>
          )}
        </Stack>
      </Panel>

      <Panel>
        <Stack gap={3}>
          <h3>{t('cpt.rules.s.propose')}</h3>
          <Text role="caption" tone="tertiary">{t('cpt.rules.proposeHint')}</Text>
          {mayPropose ? (
            <Grid min="lg" gap={3}>
              <Field label={t('cpt.rules.reason')} htmlFor="cpt-reason">
                <TextInput id="cpt-reason" value={reason} maxLength={500} onChange={(ev) => setReason(ev.target.value)} />
              </Field>
              <Row gap={3} align="end" wrap>
                <Button type="button" disabled={busy || reason.trim() === ''} onClick={() => void propose()}>{t('cpt.rules.proposeSubmit')}</Button>
              </Row>
            </Grid>
          ) : (
            <Notice tone="neutral">{t('cpt.rules.noPropose')}</Notice>
          )}
        </Stack>
      </Panel>
    </Stack>
  );
}
